// services/frontend/src/pages/SignupStep1Page.jsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import SignupFixedBottomButton from '../components/signup/SignupFixedBottomButton';
import SignupPageLayout from '../components/signup/SignupPageLayout';
import SignupTextInput from '../components/signup/SignupTextInput';
import { clearSignupDraft, patchSignupDraft } from '../utils/signupDraft';
import { markSmartHealingOriginSession } from '../utils/smartHealingAccess';

export default function SignupStep1Page() {
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const gatewayApiUrl = /^https?:\/\//i.test(apiUrl) ? apiUrl : '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitTried, setSubmitTried] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false);
  const [emailCheckStatus, setEmailCheckStatus] = useState(null);
  const [emailCheckMessage, setEmailCheckMessage] = useState('');

  useEffect(() => {
    clearSignupDraft();
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setSubmitTried(false);
    setEmailTouched(false);
    setPasswordTouched(false);
    setConfirmPasswordTouched(false);
    setPasswordFocused(false);
    setConfirmPasswordFocused(false);
    setEmailCheckStatus(null);
    setEmailCheckMessage('');
  }, []);

  useEffect(() => {
    let mounted = true;

    const applySmartHealingHandoff = async () => {
      const handoffToken = new URLSearchParams(window.location.search).get('sh_handoff');
      if (!handoffToken) {
        return;
      }

      markSmartHealingOriginSession();

      try {
        const { data } = await axios.get(`${gatewayApiUrl}/api/v1/smart-healing/handoff/${handoffToken}`);
        if (!mounted) {
          return;
        }

        const nextEmail = String(data?.email || '').trim();
        const nextName = String(data?.full_name || '').trim();
        const nextPhone = String(data?.phone_number || '').trim();
        const nextSmartHealingMemberId = String(data?.smart_healing_member_id || '').trim();

        if (nextEmail) {
          setEmail(nextEmail);
        }

        patchSignupDraft({
          userName: nextName,
          phoneNumber: nextPhone,
          smartHealingMemberId: nextSmartHealingMemberId,
        });

        if (!/^\S+@\S+\.\S+$/.test(nextEmail)) {
          return;
        }

        setEmailCheckStatus('checking');
        const availability = await axios.get(`${gatewayApiUrl}/api/v1/signup/email/availability`, {
          params: { email: nextEmail },
        });
        if (!mounted) {
          return;
        }

        if (availability.data && availability.data.available === false) {
          navigate(
            `/login?sh_handoff=${encodeURIComponent(handoffToken)}&email=${encodeURIComponent(nextEmail)}`,
            { replace: true },
          );
          return;
        }

        setEmailCheckStatus('available');
        setEmailCheckMessage('사용 가능한 이메일입니다.');
      } catch (error) {
        if (!mounted) {
          return;
        }
        setEmailCheckStatus(null);
        setEmailCheckMessage('');
      }
    };

    applySmartHealingHandoff();
    return () => {
      mounted = false;
    };
  }, [gatewayApiUrl, navigate]);

  const emailValid = useMemo(() => {
    const value = String(email || '').trim();
    if (!value) {
      return false;
    }
    return /^\S+@\S+\.\S+$/.test(value);
  }, [email]);

  const passwordValid = useMemo(() => {
    const value = String(password || '');
    if (value.length < 8 || value.length > 32) {
      return false;
    }
    const hasLetter = /[A-Za-z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSpecial = /[^A-Za-z0-9]/.test(value);
    const kinds = [hasLetter, hasNumber, hasSpecial].filter(Boolean).length;
    return kinds >= 2;
  }, [password]);

  const passwordsMatch = useMemo(() => {
    if (!password || !confirmPassword) {
      return false;
    }
    return password === confirmPassword;
  }, [password, confirmPassword]);

  const showEmailError = submitTried || emailTouched;
  const showPasswordError = submitTried || (passwordTouched && !passwordFocused);
  const showConfirmPasswordError = submitTried || (confirmPasswordTouched && !confirmPasswordFocused);

  const canNext =
    emailValid &&
    passwordValid &&
    passwordsMatch &&
    emailCheckStatus === 'available';

  const checkEmailAvailability = async () => {
    const emailTrimmed = String(email || '').trim();
    if (!emailTrimmed || !emailValid) {
      return;
    }

    setEmailCheckStatus('checking');
    setEmailCheckMessage('');
    try {
      const { data } = await axios.get(`${gatewayApiUrl}/api/v1/signup/email/availability`, {
        params: { email: emailTrimmed },
      });

      if (data && data.available === false) {
        setEmailCheckStatus('duplicate');
        setEmailCheckMessage('이미 가입된 이메일입니다.');
      } else {
        setEmailCheckStatus('available');
        setEmailCheckMessage('사용 가능한 이메일입니다.');
      }
    } catch (error) {
      setEmailCheckStatus('error');
      setEmailCheckMessage('이메일 중복 확인 중 오류가 발생했습니다. 다시 시도해 주세요.');
    }
  };

  const goNext = async () => {
    setSubmitTried(true);

    if (emailValid && emailCheckStatus !== 'available') {
      if (emailCheckStatus === 'checking') {
        setEmailCheckMessage('이메일 중복 확인 중입니다. 잠시만 기다려 주세요.');
        return;
      }
      await checkEmailAvailability();
      return;
    }

    if (!canNext) {
      return;
    }

    patchSignupDraft({
      email: String(email || '').trim(),
      password: String(password || ''),
    });

    navigate('/signup/step2');
  };

  return (
    <SignupPageLayout
      step={1}
      title="회원가입"
      onBack={() => navigate(-1)}
      bottomFixed={(
        <SignupFixedBottomButton
          label="다음"
          onClick={goNext}
          disabled={!canNext}
        />
      )}
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>기본 정보</div>

        <SignupTextInput
          type="email"
          placeholder="이메일"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setEmailCheckStatus(null);
            setEmailCheckMessage('');
            setEmailTouched(false);
          }}
          onBlur={() => {
            setEmailTouched(true);
            checkEmailAvailability();
          }}
          autoComplete="email"
          errorText={
            showEmailError && !emailValid
              ? '이메일 형식을 확인해 주세요.'
              : showEmailError && emailValid && emailCheckStatus !== 'available'
                ? (emailCheckStatus === 'duplicate'
                  ? '이미 가입된 이메일입니다. 다른 이메일로 다시 시도해 주세요.'
                  : emailCheckStatus === 'checking'
                    ? '이메일 중복 확인 중입니다. 잠시만 기다려 주세요.'
                    : '이메일 중복 확인을 진행해 주세요. 입력 후 포커스를 이동하면 자동 확인됩니다.')
                : ''
          }
        />
        {emailCheckStatus === 'available' && (
          <div style={{ color: '#16a34a', fontSize: 13 }}>
            {emailCheckMessage || '사용 가능한 이메일입니다.'}
          </div>
        )}
        {emailCheckStatus === 'error' && (
          <div style={{ color: '#b91c1c', fontSize: 13 }}>{emailCheckMessage}</div>
        )}

        <SignupTextInput
          type="password"
          placeholder="비밀번호(8~32자, 영문/숫자/특수문자 2종 이상)"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
            setPasswordTouched(false);
          }}
          onFocus={() => setPasswordFocused(true)}
          onBlur={() => {
            setPasswordFocused(false);
            setPasswordTouched(true);
          }}
          autoComplete="new-password"
          errorText={
            showPasswordError && !passwordValid
              ? '비밀번호는 8~32자, 영문/숫자/특수문자 중 2종 이상을 포함해야 합니다.'
              : ''
          }
          helperText={
            !showPasswordError
              ? '비밀번호는 8~32자이며 영문/숫자/특수문자 중 2종 이상을 포함해 주세요.'
              : ''
          }
        />

        <SignupTextInput
          type="password"
          placeholder="비밀번호 확인"
          value={confirmPassword}
          onChange={(event) => {
            setConfirmPassword(event.target.value);
            setConfirmPasswordTouched(false);
          }}
          onFocus={() => setConfirmPasswordFocused(true)}
          onBlur={() => {
            setConfirmPasswordFocused(false);
            setConfirmPasswordTouched(true);
          }}
          autoComplete="new-password"
          errorText={showConfirmPasswordError && !passwordsMatch ? '비밀번호가 일치하지 않습니다.' : ''}
          helperText={!showConfirmPasswordError ? '비밀번호를 한번 더 입력해 주세요.' : ''}
        />
      </div>
    </SignupPageLayout>
  );
}
